import type { AdtagEnv } from "@sphtech/web2-ads";

// The web2-ads SDK canary is scoped to two pages: the companies-markets listing
// and one companies-markets article. Every other path (including other
// companies-markets articles) resolves to `undefined`, leaving the legacy ad
// stack untouched.
export const BT_SDK_ADS_COMPANIES_MARKETS_PATH = "/companies-markets";
export const BT_SDK_ADS_CANARY_PATH =
  "/companies-markets/just-had-most-amazing-coffee-coffeelovers-musiclife-newrelease-artist-movie-night-filmclub-let-me";

export const BT_SDK_ADS_CANARY_PATHS = [
  BT_SDK_ADS_COMPANIES_MARKETS_PATH,
  BT_SDK_ADS_CANARY_PATH,
] as const;

// Router-state flag tagged onto the client-side navigations the article page
// fires while infinite-scrolling (see useHookUpdateUrlArticle). It is what lets
// the canary config latch survive a scroll from the canary article onto a
// later, unconfigured sibling article.
export const BT_SDK_ADS_VIRTUAL_ARTICLE_NAVIGATION_STATE_KEY =
  "btSdkAdsVirtualArticleNavigation";

export type BtSdkAdsCanaryConfig = {
  articleId?: string;
  articleTags?: readonly string[];
  page: string;
  pageType: "article" | "listing";
  siteKey: "bt";
  url: string;
};

// To add another page to the canary later, add its path to
// BT_SDK_ADS_CANARY_PATHS above and a matching entry here. That alone enables
// the SDK on a fresh load / SSR of that exact path — the config type,
// getBtSdkAdsCanaryConfig, the context provider, BtSdkAdsShell and Ad all
// already handle both pageTypes. For article canaries the virtual-article
// *scroll-continuation* latch (resolveBtSdkAdsCanarySessionConfig +
// BT_SDK_ADS_VIRTUAL_ARTICLE_NAVIGATION_STATE, tagged in useHookUpdateUrlArticle
// and applied in BtSdkAdsCanaryConfigProvider) keeps the SDK active — and the
// legacy ad stack disabled — while infinite-scrolling from the canary article
// onto later, unconfigured sibling articles.
export const BT_SDK_ADS_CANARY_CONFIGS = {
  [BT_SDK_ADS_COMPANIES_MARKETS_PATH]: {
    page: "companies-markets",
    pageType: "listing",
    siteKey: "bt",
    url: BT_SDK_ADS_COMPANIES_MARKETS_PATH,
  },
  [BT_SDK_ADS_CANARY_PATH]: {
    articleId: "2212299",
    articleTags: [],
    page: "companies-markets",
    pageType: "article",
    siteKey: "bt",
    url: BT_SDK_ADS_CANARY_PATH,
  },
} as const satisfies Record<
  (typeof BT_SDK_ADS_CANARY_PATHS)[number],
  BtSdkAdsCanaryConfig
>;

export const BT_SDK_ADS_CANARY_CONFIG =
  BT_SDK_ADS_CANARY_CONFIGS[BT_SDK_ADS_CANARY_PATH];

export function getBtSdkAdsAdtagEnv(environment: string): AdtagEnv {
  switch (environment) {
    case "local":
    case "development-env":
      return "dev";

    case "staging-env":
      return "stg";

    case "production-env":
    default:
      return "prod";
  }
}

type ResolveBtSdkAdsCanarySessionConfigParams = {
  pathname: string;
  currentConfig?: BtSdkAdsCanaryConfig;
  isVirtualArticleNavigation: boolean;
};

export const BT_SDK_ADS_VIRTUAL_ARTICLE_NAVIGATION_STATE = {
  [BT_SDK_ADS_VIRTUAL_ARTICLE_NAVIGATION_STATE_KEY]: true,
} as const;

// Merge the virtual-nav flag into whatever router state a navigation already
// carries, so tagging an infinite-scroll navigation never clobbers unrelated
// state.
export function getBtSdkAdsVirtualArticleNavigationState(
  currentState?: unknown
): Record<string, unknown> {
  if (
    currentState &&
    typeof currentState === "object" &&
    !Array.isArray(currentState)
  ) {
    return {
      ...currentState,
      ...BT_SDK_ADS_VIRTUAL_ARTICLE_NAVIGATION_STATE,
    };
  }

  return BT_SDK_ADS_VIRTUAL_ARTICLE_NAVIGATION_STATE;
}

export function isBtSdkAdsVirtualArticleNavigationState(
  state: unknown
): boolean {
  if (!state || typeof state !== "object" || Array.isArray(state)) return false;

  if (
    !Object.prototype.hasOwnProperty.call(
      state,
      BT_SDK_ADS_VIRTUAL_ARTICLE_NAVIGATION_STATE_KEY
    )
  ) {
    return false;
  }

  return (
    (state as Record<string, unknown>)[
      BT_SDK_ADS_VIRTUAL_ARTICLE_NAVIGATION_STATE_KEY
    ] === true
  );
}

// Resolve the canary config for the current SPA location. A configured canary
// pathname always wins (fresh load / SSR / real navigation). Otherwise, if this
// is a virtual infinite-scroll navigation, keep the latched config so SDK ads
// survive the scroll onto an unconfigured sibling article. Any other navigation
// (no virtual-nav state, non-canary pathname) clears the canary — the legacy ad
// stack takes over. This means the latch only ever engages for a session that
// *started* on a canary path, since currentConfig is undefined otherwise.
export function resolveBtSdkAdsCanarySessionConfig({
  pathname,
  currentConfig,
  isVirtualArticleNavigation,
}: ResolveBtSdkAdsCanarySessionConfigParams): BtSdkAdsCanaryConfig | undefined {
  const pathnameConfig = getBtSdkAdsCanaryConfig(pathname);

  if (pathnameConfig) return pathnameConfig;
  if (isVirtualArticleNavigation) return currentConfig;

  return undefined;
}

export function normalizeBtSdkAdsPath(pathname: string): string {
  const normalizedPath = pathname.replace(/\/+$/, "");

  return normalizedPath || "/";
}

export function getBtSdkAdsCanaryConfig(
  pathname: string
): BtSdkAdsCanaryConfig | undefined {
  const normalizedPath = normalizeBtSdkAdsPath(pathname);

  return BT_SDK_ADS_CANARY_CONFIGS[
    normalizedPath as keyof typeof BT_SDK_ADS_CANARY_CONFIGS
  ];
}

export function isBtSdkAdsCanaryPath(pathname: string): boolean {
  return typeof getBtSdkAdsCanaryConfig(pathname) !== "undefined";
}
